import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { SelectField, TextField, CurrencyField, DateField, PercentageField } from '@roc/ui';
import { ExtensionDetailsStore } from '../../../stores/documentForms/extensionDetailsStore';
import { format, isValid } from 'date-fns';
import { formatPrecision } from '@roc/feature-utils';
import { useEffect, useState } from 'react';

interface Props {
  store: ExtensionDetailsStore;
}

const toNumber = (value: any) =>
  parseFloat(String(value ?? '').replace(/[^0-9.]/g, '')) || 0;

const feeToPercentage = (fee: number, loanAmount: number): number | string =>
  fee > 0 && loanAmount > 0 ? formatPrecision((fee / loanAmount) * 100, 3) : '';

const percentageToFee = (percentage: number, loanAmount: number) =>
  formatPrecision((percentage / 100) * loanAmount, 2);

export const ExtensionDetailsForm = observer(({ store }: Props) => {
  const extensionDurationOptions = [
    { label: '3 months', value: '3' },
    { label: '6 months', value: '6' },
  ];
  if (store.globalStore.isInternalPortal) {
    extensionDurationOptions.push({ label: 'Custom', value: 'custom' });
  }


  const totalLoanAmount = toNumber(store.totalLoanAmount);
  const maxAllowed = totalLoanAmount * 0.1;
  const currentFee = toNumber(store.totalExtensionFee);
  const isFeeTooHigh = currentFee > maxAllowed && maxAllowed > 0;

  // UI-only calculator: the fee is the persisted value, this just mirrors it as a percentage of the total loan amount.
  const [feePercentage, setFeePercentage] = useState<number | string>(
    feeToPercentage(currentFee, totalLoanAmount)
  );

  // Re-sync when the fee changes outside this field.
  useEffect(() => {
    const derived = feeToPercentage(currentFee, totalLoanAmount);
    if (toNumber(feePercentage) !== toNumber(derived)) {
      setFeePercentage(derived);
    }
  }, [currentFee, totalLoanAmount]);

  const handleFeeChange = (value: any) => {
    const nextFee = value?.toString() ?? '';
    if (nextFee === store.totalExtensionFee) return;
    store.setField('totalExtensionFee', nextFee);
    setFeePercentage(feeToPercentage(toNumber(value), totalLoanAmount));
  };

  const handleFeePercentageChange = (value: any) => {
    const nextPercentage = value ?? '';
    if (nextPercentage.toString() === feePercentage.toString()) return;
    setFeePercentage(nextPercentage);
    store.setField(
      'totalExtensionFee',
      percentageToFee(toNumber(value), totalLoanAmount).toString()
    );
  };

  const manageDate = (fieldName: string, value: any) => {
    // Handles both Date and event types
    if (isValid(value)) {
      console.log('Valid date selected:', value);
      store.setField(fieldName, value);
    } else {
      store.setField(fieldName, null);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={3}>
        <TextField
          disabled
          fullWidth
          standaloneLabel
          variant="outlined"
          label="Request Date"
          testId="requestDate"
          value={store.requestDate}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          disabled
          fullWidth
          standaloneLabel
          variant="outlined"
          testId="nextDueDate"
          label="Next Due Date"
          value={store.nextDueDate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          disabled
          fullWidth
          standaloneLabel
          variant="outlined"
          testId="existingMaturityDate"
          label="Existing Maturity Date"
          value={store.existingMaturityDate}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <SelectField
          required
          fullWidth
          standaloneLabel
          variant="outlined"
          label="Extension (Duration)"
          testId="extensionDuration"
          options={extensionDurationOptions}
          value={store.extensionDuration}
          onChange={(value) => store.setField('extensionDuration', value)}
          disabled={store.isReadOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DateField
          format="MM/dd/yyyy"
          testId="newMaturityDate"
          required
          standaloneLabel
          inputVariant="outlined"
          label="New Maturity Date"
          value={store.newMaturityDate}
          onChange={(value) => manageDate('customNewMaturityDate', value)}
          disableFuture={false}
          fullWidth
          disabled={store.disableNewMaturityDate || store.isReadOnly}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <CurrencyField
          required
          fullWidth
          standaloneLabel
          variant="outlined"
          error={isFeeTooHigh}
          name="totalExtensionFee"
          label="Total Extension Fee"
          testId="totalExtensionFee"
          value={store.totalExtensionFee}
          disabled={store.isReadOnly}
          onChange={(_, value) => handleFeeChange(value)}
          errorText={isFeeTooHigh ? 'The extension fee cannot exceed 10% of the total loan amount.' : undefined}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <PercentageField
          fullWidth
          standaloneLabel
          decimalScale={3}
          variant="outlined"
          name="feePercentage"
          label="Percent Calculator"
          testId="feePercentage"
          value={feePercentage}
          disabled={store.isReadOnly || totalLoanAmount <= 0}
          onChange={(_, value) => handleFeePercentageChange(value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <CurrencyField
          disabled
          fullWidth
          standaloneLabel
          variant="outlined"
          name="totalLoanAmount"
          label="Total Loan Amount"
          testId="totalLoanAmount"
          value={store.totalLoanAmount}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Notes"
          standaloneLabel
          testId="notes"
          variant="outlined"
          value={store.notes}
          disabled={store.isReadOnly}
          onChange={(e) => store.setField('notes', e.target.value)}
        />
      </Grid>
    </Grid>
  );
});